import React, { useEffect, useState } from "react";
import "./HotelInfo.css";
import { useLocation, useNavigate } from "react-router-dom";
import { BACKEND_URL } from "../../services/Helper";
import secimage from "../../Assets/hero.jpg";

const isDirectVideo = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return (
        lower.includes('cloudinary.com') ||
        lower.endsWith('.mp4') ||
        lower.endsWith('.webm') ||
        lower.endsWith('.mov')
    );
};

const HotelInfo = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { hotels } = location.state || {};

    const [activeTab, setActiveTab] = useState("menu");

    // Menu
    const [hotelmenu, setHotelMenu] = useState([]);

    // Offers
    const [shopOffers, setShopOffers] = useState([]);
    const [offersLoading, setOffersLoading] = useState(false);
    const [offersFetched, setOffersFetched] = useState(false);

    // Reviews
    const [reviews, setReviews] = useState([]);
    const [avgRating, setAvgRating] = useState(0);
    const [totalReviews, setTotalReviews] = useState(0);
    const [myRating, setMyRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [myReviewText, setMyReviewText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [reviewMsg, setReviewMsg] = useState("");
    const [mediaFiles, setMediaFiles] = useState([]);
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [uploadingMedia, setUploadingMedia] = useState(false);

    const fetchMenu = async () => {
        try {
            const res = await fetch(`${BACKEND_URL}/nearbe/getShopMenu`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ shopId: hotels._id }),
            });
            const data = await res.json();
            setHotelMenu(data.shopMenu || []);
        } catch (e) { console.error(e); }
    };

    const fetchReviews = async () => {
        try {
            const res = await fetch(`${BACKEND_URL}/nearbe/getreviews/${hotels._id}`);
            const data = await res.json();
            setReviews(data.reviews || []);
            setAvgRating(data.avgRating || 0);
            setTotalReviews(data.total || 0);
        } catch (e) { console.error(e); }
    };

    const fetchOffers = async () => {
        if (offersFetched) return;
        setOffersLoading(true);
        try {
            const res = await fetch(`${BACKEND_URL}/nearbe/getalloffer`);
            const data = await res.json();
            const all = data.allOffer || [];
            const mine = all.filter((o) => {
                const pid = o.postedBy?._id || o.postedBy;
                return String(pid) === String(hotels._id);
            });
            setShopOffers(mine);
            setOffersFetched(true);
        } catch (e) { console.error(e); }
        setOffersLoading(false);
    };

    useEffect(() => {
        fetchMenu();
        fetchReviews();
    }, [hotels]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === "offers") fetchOffers();
    };

    const handleMediaChange = (e) => {
        const files = Array.from(e.target.files).slice(0, 4);
        setMediaFiles(files);
        setMediaPreviews(files.map(f => URL.createObjectURL(f)));
    };

    const removeMedia = (idx) => {
        setMediaFiles(prev => prev.filter((_, i) => i !== idx));
        setMediaPreviews(prev => prev.filter((_, i) => i !== idx));
    };

    const uploadToCloudinary = async (file) => {
        const form = new FormData();
        form.append("file", file);
        form.append("upload_preset", process.env.REACT_APP_CLOUDINARY_PRESET);
        form.append("cloud_name", process.env.REACT_APP_CLOUDINARY_CLOUD);
        const res = await fetch(
            `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD}/image/upload`,
            { method: "POST", body: form }
        );
        const data = await res.json();
        return data.secure_url;
    };

    const submitReview = async () => {
        // Login check FIRST
        const token = localStorage.getItem("nearbeusertoken");
        if (!token) { navigate("/userlogin"); return; }
        if (!myRating) { setReviewMsg("Please select a star rating first."); return; }
        setSubmitting(true);
        setReviewMsg("");
        try {
            let mediaUrls = [];
            if (mediaFiles.length > 0) {
                setUploadingMedia(true);
                mediaUrls = await Promise.all(mediaFiles.map(uploadToCloudinary));
                setUploadingMedia(false);
            }
            const res = await fetch(`${BACKEND_URL}/nearbe/addreview`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: token },
                body: JSON.stringify({ shopId: hotels._id, rating: myRating, text: myReviewText, mediaUrls }),
            });
            if (res.ok) {
                setReviewMsg("✅ Review submitted! Thank you.");
                setMyRating(0); setMyReviewText(""); setMediaFiles([]); setMediaPreviews([]);
                fetchReviews();
            } else {
                setReviewMsg("❌ Could not submit. Please login as a user (not shop owner).");
            }
        } catch (e) { setReviewMsg("❌ Error submitting review."); }
        setSubmitting(false);
        setUploadingMedia(false);
    };

    const renderStars = (rating, size = 15) => [1, 2, 3, 4, 5].map((s) => (
        <span key={s} style={{ fontSize: size, color: s <= Math.round(rating) ? "#F97316" : "#D1D5DB", lineHeight: 1 }}>★</span>
    ));

    const tabs = [
        { key: "menu",    label: "🍽️ Menu" },
        { key: "offers",  label: "🏷️ Offers" },
        { key: "reel",    label: "🎬 Reel" },
        { key: "info",    label: "ℹ️ Info" },
        { key: "reviews", label: `⭐ Reviews${totalReviews > 0 ? ` (${totalReviews})` : ""}` },
    ];

    return (
        <div className="hotelinfo">

            {/* ── HERO HEADER ── */}
            <div className="hotelinfo-upper">
                <div className="hotelinfo-name">
                    <h1>{hotels.shopName} <span>({hotels.category})</span></h1>
                    {hotels.distance !== null && hotels.distance !== undefined && (
                        <p>📍 {hotels.distance.toFixed(1)} km away from your location</p>
                    )}
                    {avgRating > 0 && (
                        <div className="hi-header-rating">
                            {renderStars(avgRating, 13)}
                            <span className="hi-header-rating-num">{avgRating} ({totalReviews})</span>
                        </div>
                    )}
                </div>
                <div className="hi-header-contact">
                    <p>📞 {hotels.contactNumber}</p>
                    {hotels.address && <p>📍 {hotels.address}</p>}
                </div>
            </div>

            {/* ── TAB BAR ── */}
            <div className="hi-tabs">
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        className={`hi-tab${activeTab === t.key ? " hi-tab-active" : ""}`}
                        onClick={() => handleTabChange(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ── TAB CONTENT ── */}
            <div className="hi-tab-content">

                {/* MENU TAB */}
                {activeTab === "menu" && (
                    <div className="hi-menu-layout">
                        {/* LEFT: menu items */}
                        <div className="hi-menu-list">
                            {hotelmenu.length === 0 ? (
                                <div className="hi-empty-state">
                                    <span>🍽️</span>
                                    <p>No menu items added yet.</p>
                                </div>
                            ) : (
                                hotelmenu.map((item) => {
                                    const desc = item?.itemdescription;
                                    const showDesc = desc && desc.trim() !== "" && desc.trim().toLowerCase() !== "na";
                                    const prep = item?.prepTime;
                                    const showPrep = prep && String(prep).trim() !== "" && String(prep).trim().toLowerCase() !== "na" && String(prep).trim() !== "0";
                                    const avail = item?.availability;
                                    const isAvail = avail && avail.toLowerCase() !== "na" && avail.toLowerCase() !== "not available";
                                    return (
                                        <div key={item._id} className="hi-mcard">
                                            <div className="hi-mcard-body">
                                                <div className="hi-mcard-top">
                                                    <h3 className="hi-mcard-name">{item?.itemName}</h3>
                                                    {item?.offerPercentage > 0 && (
                                                        <span className="hi-mcard-off">{item.offerPercentage}% OFF</span>
                                                    )}
                                                </div>
                                                {showDesc && <p className="hi-mcard-desc">{desc}</p>}
                                                <div className="hi-mcard-footer">
                                                    <span className="hi-mcard-price">₹{item?.itemPrice}</span>
                                                    {showPrep && (
                                                        <span className="hi-mcard-prep">⏱ {prep} mins</span>
                                                    )}
                                                    {avail && avail.toLowerCase() !== "na" && (
                                                        <span className={`hi-mcard-av ${isAvail ? "hi-mcard-av-yes" : "hi-mcard-av-no"}`}>
                                                            {isAvail ? "● Available" : "● Not Available"}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="hi-mcard-img">
                                                {item?.itemImage ? (
                                                    <img src={item.itemImage} alt={item.itemName} />
                                                ) : (
                                                    <div className="hi-mcard-img-placeholder" />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* RIGHT: shop image panel */}
                        <div className="hi-menu-shopimg">
                            <div className="hi-menu-shopimg-inner">
                                {hotels.shopImage ? (
                                    <img src={hotels.shopImage} alt={hotels.shopName} className="hi-menu-shop-photo" />
                                ) : (
                                    <div className="hi-menu-shop-placeholder" />
                                )}
                                <div className="hi-menu-shopimg-info">
                                    <p className="hi-menu-shopimg-name">{hotels.shopName}</p>
                                    <p className="hi-menu-shopimg-cat">{hotels.category}</p>
                                    {hotels.homeDelivery && (
                                        <span className="hi-menu-delivery-badge">🚚 Free Delivery</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* OFFERS TAB */}
                {activeTab === "offers" && (
                    <div className="hi-offers-wrap">
                        {offersLoading ? (
                            <p className="no-offer-msg">⏳ Loading offers...</p>
                        ) : shopOffers.length === 0 ? (
                            <div className="hi-empty-state">
                                <span>🏷️</span>
                                <p>No active offers for this shop right now.</p>
                            </div>
                        ) : (
                            shopOffers.map((o, i) => {
                                const isShop = o.type === "shop_offer";
                                const levelLabel = o.level === 3 ? "🔥 Promoted" : o.level === 2 ? "🏷️ Shop Deal" : "⚡ Item Deal";
                                const title = isShop ? o.title : o.itemName;
                                const desc  = isShop ? o.description : o.itemDescription;
                                const pct   = isShop ? o.percentage : o.offerPercentage;
                                return (
                                    <div key={i} className={`hi-offer-card hi-offer-l${o.level || 2}`}>
                                        <span className="hi-offer-badge">{levelLabel}</span>
                                        <h3 className="hi-offer-title">{title}</h3>
                                        {desc && <p className="hi-offer-desc">{desc}</p>}
                                        {pct && <p className="hi-offer-pct">{pct}% OFF</p>}
                                        {o.validTill && (
                                            <p className="hi-offer-valid">
                                                Valid till: {new Date(o.validTill).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                            </p>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {/* REEL TAB */}
                {activeTab === "reel" && (
                    <div className="hi-reel-wrap">
                        {hotels.reelLink ? (
                            isDirectVideo(hotels.reelLink) ? (
                                <div className="hi-reel-video-wrap">
                                    <h3 className="hi-reel-title">{hotels.shopName}'s Shop Reel</h3>
                                    <video
                                        src={hotels.reelLink}
                                        controls
                                        playsInline
                                        className="hi-reel-video"
                                    />
                                </div>
                            ) : (
                                <div className="hi-reel-card">
                                    <div className="hi-reel-icon">🎬</div>
                                    <h3 className="hi-reel-title">{hotels.shopName}'s Shop Reel</h3>
                                    <p className="hi-reel-sub">
                                        Watch this shop's reel on Instagram or YouTube.
                                    </p>
                                    <a
                                        href={hotels.reelLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hi-reel-btn"
                                    >
                                        ▶ Watch Shop Reel
                                    </a>
                                    <p className="hi-reel-note">Opens on Instagram / YouTube externally</p>
                                </div>
                            )
                        ) : (
                            <div className="hi-empty-state">
                                <span>🎬</span>
                                <p>This shop hasn't shared a reel yet.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* INFO TAB */}
                {activeTab === "info" && (
                    <div className="hi-info-wrap">
                        <div className="hi-info-card">
                            <div className="hi-info-row">
                                <span>🏪</span>
                                <div><strong>Shop Name</strong><p>{hotels.shopName}</p></div>
                            </div>
                            <div className="hi-info-row">
                                <span>📂</span>
                                <div><strong>Category</strong><p>{hotels.category}</p></div>
                            </div>
                            {hotels.address && (
                                <div className="hi-info-row">
                                    <span>📍</span>
                                    <div><strong>Address</strong><p>{hotels.address}</p></div>
                                </div>
                            )}
                            <div className="hi-info-row">
                                <span>📞</span>
                                <div><strong>Contact</strong><p>{hotels.contactNumber}</p></div>
                            </div>
                            {hotels.distance !== null && hotels.distance !== undefined && (
                                <div className="hi-info-row">
                                    <span>🗺️</span>
                                    <div><strong>Distance</strong><p>{hotels.distance.toFixed(1)} km from your location</p></div>
                                </div>
                            )}
                            {hotels.mallName && (
                                <div className="hi-info-row">
                                    <span>🏬</span>
                                    <div>
                                        <strong>Mall</strong>
                                        <p>{hotels.mallName}
                                            {(hotels.floor || hotels.shopUnit) && (
                                                <span style={{ color: '#1D4ED8', fontWeight: 600, marginLeft: 6 }}>
                                                    · {[hotels.floor, hotels.shopUnit].filter(Boolean).join(' · ')}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            )}
                            <div className="hi-info-row">
                                <span>🚚</span>
                                <div>
                                    <strong>Home Delivery</strong>
                                    <p style={{ color: hotels.homeDelivery ? '#10B981' : '#9CA3AF', fontWeight: hotels.homeDelivery ? 700 : 400 }}>
                                        {hotels.homeDelivery ? 'Free Home Delivery Available' : 'Not Available'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* REVIEWS TAB */}
                {activeTab === "reviews" && (
                    <div className="hi-reviews-wrap">
                        <div className="hi-rating-summary">
                            <div className="hi-big-rating">{avgRating > 0 ? avgRating : "–"}</div>
                            <div>
                                <div style={{ display: "flex", gap: 2 }}>{renderStars(avgRating, 22)}</div>
                                <p className="hi-total-reviews">{totalReviews} review{totalReviews !== 1 ? "s" : ""}</p>
                            </div>
                        </div>

                        {!localStorage.getItem("nearbeusertoken") && (
                            <div className="hi-login-prompt">
                                <span>🔒</span>
                                <p>Sign in as a customer to leave a review</p>
                                <button className="hi-login-prompt-btn" onClick={() => navigate("/userlogin")}>
                                    Sign In
                                </button>
                            </div>
                        )}

                        <div className="hi-add-review">
                            <h3>Leave a Review</h3>
                            <div className="hi-star-select">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <span
                                        key={s}
                                        className={`hi-star${s <= (hoverRating || myRating) ? " hi-star-on" : ""}`}
                                        onClick={() => setMyRating(s)}
                                        onMouseEnter={() => setHoverRating(s)}
                                        onMouseLeave={() => setHoverRating(0)}
                                    >★</span>
                                ))}
                            </div>
                            <textarea
                                className="hi-review-textarea"
                                placeholder="Share your experience (optional)..."
                                value={myReviewText}
                                onChange={(e) => setMyReviewText(e.target.value)}
                                rows={3}
                            />

                            {/* Media upload */}
                            <label className="hi-media-label">
                                📷 Add Photos (up to 4)
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    style={{ display: "none" }}
                                    onChange={handleMediaChange}
                                />
                            </label>
                            {mediaPreviews.length > 0 && (
                                <div className="hi-media-previews">
                                    {mediaPreviews.map((src, i) => (
                                        <div key={i} className="hi-media-thumb-wrap">
                                            <img src={src} alt="preview" className="hi-media-thumb" />
                                            <button className="hi-media-remove" onClick={() => removeMedia(i)}>✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {reviewMsg && <p className="hi-review-msg">{reviewMsg}</p>}
                            <button className="hi-submit-review" onClick={submitReview} disabled={submitting}>
                                {uploadingMedia ? "Uploading photos…" : submitting ? "Submitting…" : "Submit Review"}
                            </button>
                        </div>

                        {reviews.length === 0 ? (
                            <div className="hi-empty-state">
                                <span>💬</span>
                                <p>No reviews yet. Be the first to review!</p>
                            </div>
                        ) : (
                            reviews.map((r, i) => (
                                <div key={i} className="hi-review-card">
                                    <div className="hi-review-top">
                                        <div className="hi-reviewer-avatar">
                                            {(r.userId?.name || r.userId?.phoneNumber || "U")[0].toUpperCase()}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <p className="hi-reviewer-name">
                                                {r.userId?.name || r.userId?.phoneNumber || "User"}
                                            </p>
                                            <div style={{ display: "flex", gap: 2 }}>{renderStars(r.rating, 13)}</div>
                                        </div>
                                        <span className="hi-review-date">
                                            {new Date(r.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                        </span>
                                    </div>
                                    {r.text && <p className="hi-review-body">{r.text}</p>}
                                    {r.mediaUrls?.length > 0 && (
                                        <div className="hi-review-images">
                                            {r.mediaUrls.map((url, j) => (
                                                <img key={j} src={url} alt="review" className="hi-review-img"
                                                    onClick={() => window.open(url, "_blank")}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}

            </div>
        </div>
    );
};

export default HotelInfo;
